"use client";

import React, { use, useCallback, useEffect, useState } from "react";
import ButtonToggle from "@/components/Elements/ButtonToggle";
import Heading from "@/components/Elements/Heading";
import { signOut } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import Skeleton from "react-loading-skeleton";
import toast from "react-hot-toast";
import useUserStore from "@/stores/useUserStore";
import MyPagesNotifications from "@/components/MyPages/MyPagesNotifications";
import useSWR from "swr";
import { getUserInfo } from "@/actions/user";
import { useShallow } from "zustand/react/shallow";
import "react-loading-skeleton/dist/skeleton.css";
import useMediaQuery from "@/hooks/useMediaQuery";

interface MyPagesClientProps {
  path: any;
  routes: any;
  children: any;
  userId: any;
}

const MyPagesClient: React.FC<MyPagesClientProps> = ({
  path,
  routes,
  children,
  userId,
}) => {
  const { user, clearSession, userInfo } = useUserStore(
    useShallow((state) => state)
  );
  const router = useRouter();
  const [isLoading, setIsloading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const searchParams = useSearchParams();
  const isMobile = useMediaQuery("(max-width: 640px)");

  const goToPage = searchParams?.get("page");

  useEffect(() => {
    setMounted(true);
  }, []);

  const currentPath = path[0] || "";

  const menuLinks = routes.map((route: any) => {
    return {
      label: route.title,
      onClick: () => setCurrentPage(route.slug),
      selected: currentPath === route.slug,
      identifier: route?.myPagesPageType?.type,
      slug: route.slug,
      href: `/mina-sidor/${route.slug}`,
    };
  });

  useEffect(() => {
    const linkExists = menuLinks?.find((link: any) => link.slug === goToPage);

    if (linkExists) {
      setIsloading(true);
      router.replace(`/mina-sidor/${goToPage}`);
    }
  }, [goToPage]);

  const setCurrentPage = useCallback(
    (page: string) => {
      setIsloading(true);
      router.replace(`/mina-sidor/${page}`);
    },
    [router]
  );

  const logout = () => {
    toast.loading("Loggar ut...", {
      id: "logout-user",
    });
    clearSession();
    signOut();
  };

  const menuLinksCustom = isMobile
    ? menuLinks.map((link: any) => {
        return {
          ...link,
          label: `Sida: ${link.label}`,
        };
      })
    : menuLinks;

  return (
    <div className="wrapper">
      <div className="content-wrapper">
        <div className="block md:hidden mb-4 mb-8 font-bold">
          <ButtonToggle buttons={menuLinksCustom} useLinks />
        </div>
        <div className="border border-gray-ultra-light -mt-6 py-6">
          <div className="flex flex-col md:flex-row justify-between">
            <Heading className="!font-light text-2xl md:text-xxl">
              Välkommen
              {userInfo?.infoName ? (
                <>
                  {", "}
                  <span className="font-bold block md:inline-block">
                    {userInfo?.infoName}
                  </span>
                </>
              ) : (
                ""
              )}
            </Heading>

            <div
              className="text-blue text-16 font-semibold flex flex-row items-center space-x-3 group"
              role="button"
              onClick={() => logout()}
            >
              <span>Logga ut</span>
              <svg
                width="24"
                height="48"
                viewBox="0 0 24 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="group-hover:translate-x-[2px] duration-500 transition-all"
              >
                <path
                  d="M17 20L15.59 21.41L17.17 23H9V25H17.17L15.59 26.58L17 28L21 24L17 20ZM5 17H12V15H5C3.9 15 3 15.9 3 17V31C3 32.1 3.9 33 5 33H12V31H5V17Z"
                  fill="#0A5599"
                />
              </svg>
            </div>
          </div>
        </div>
        <div className="flex flex-col">
          <MyPagesNotifications
            menuLinks={menuLinks}
            setCurrentPage={setCurrentPage}
          />

          <div className="flex flex-col md:space-y-8">
            <div className="hidden md:block">
              <ButtonToggle buttons={menuLinks} useLinks />
            </div>
            {!mounted || isLoading ? (
              <div className="flex flex-col space-y-6">
                <Skeleton height={100} />
                <Skeleton height={100} />
                <Skeleton height={100} />
              </div>
            ) : (
              <div className="self-stretch flex flex-col items-start justify-start ">
                {mounted && children}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyPagesClient;
